/**
 * `POST /v1/artwork` and `POST /v1/artwork/batches` — the request shapes.
 *
 * ## Three places this deviates from §5's one-line sketch, each recorded in the Drift Log
 *
 * §5 spells the endpoint as `POST {productId|gtin, symbology:'qr'|'gs1datamatrix',
 * xdim_mm, ecc?, format:'svg'|'pdf'|'eps'|'png', qualifiers?}`. That line predates every
 * other convention this API grew, and three parts of it are wrong against the tree rather
 * than merely terse:
 *
 *   1. **`productId` only — no raw `gtin` alternative.** §5's own c36 note settles this
 *      three paragraphs further down: *"This is the only door a raw GTIN-14 enters the
 *      authenticated API"*, said of `POST /products`, having measured that *"every other
 *      resource takes a `productId` and reads the code off the product row."* A `gtin`
 *      member here would open a second such door, and it would have to grow its own
 *      check-digit refusal, its own claim lookup and its own ownership question — three
 *      rules that the product row already answers. The gates need the row regardless: §8.1
 *      rows 5 and 6 are *about* the row's status.
 *   2. **`symbology: "datamatrix"`, not `"gs1datamatrix"`.** `packages/barcode` named the
 *      two members at c87 and its emitters, goldens and decode loop all spell it this way.
 *      A wire that disagreed with the engine would need a translation nobody asked for.
 *   3. **`applicationPreset` exists at all.** §5 names no such member and §8.1 #4 requires
 *      one — *"below the GenSpecs minimum **for the selected application preset**"*, with
 *      an override available *"only by deliberately switching to a declared non-POS
 *      application — an application choice, not a checkbox"*. A gate whose threshold is
 *      chosen by an argument that does not exist on the request cannot be what §8.1
 *      describes. It defaults to `retail-pos`, which is the one preset that ships.
 *
 * ## The two vocabularies below are spelled here rather than imported, and that is forced
 *
 * `SYMBOLOGIES`, `OUTPUT_FORMATS` and `QR_ECC_LEVELS` all exist in `packages/barcode`, and
 * importing them would be the obvious move. **`packages/shared` may not import
 * `packages/barcode`** — not by preference but by two rules with exit codes:
 * `site-static-no-server-packages-reachable` and `spa-has-no-server-packages-reachable`
 * forbid `apps/site -> … -> packages/barcode` and `apps/app -> … -> packages/barcode` **at
 * any distance**, and both apps import this package. One import here would turn two CI
 * rules red in two apps that never mentioned a barcode.
 *
 * So these are transcriptions, and they get the treatment every transcribed vocabulary in
 * this repo gets (ruled 2026-08-04): the constant here, the constant there, and a test that
 * asserts the two are **set-equal**. That test lives in `apps/api`, which is the one package
 * permitted to see both sides. Without it these are two lists that agree today.
 */
import { z } from "zod";
import { CHARSET_X } from "./patterns.js";
/**
 * The 2D symbologies. Set-equal to `packages/barcode`'s `SYMBOLOGIES` — asserted, see the
 * header. No linear symbologies: §8 generates none.
 */
export const ARTWORK_SYMBOLOGIES = ["qr", "datamatrix"];
/** Output formats. Set-equal to `packages/barcode`'s `OUTPUT_FORMATS`. */
export const ARTWORK_FORMATS = ["svg", "eps", "pdf", "png"];
/** QR error correction, weakest first. Set-equal to `packages/barcode`'s `QR_ECC_LEVELS`. */
export const ARTWORK_ECC_LEVELS = ["L", "M", "Q", "H"];
/**
 * The declared application areas. Set-equal to `packages/barcode`'s `APPLICATION_PRESETS`
 * keys.
 *
 * One member, and a second is *"a table of cited numbers"* rather than an escape valve —
 * `x-dimension.ts` argues that at length, and the argument is §8.1 #4's: an application
 * whose minimum is "none" would be the checkbox §8.1 refuses, wearing an enum's clothes.
 */
export const ARTWORK_APPLICATION_PRESETS = ["retail-pos"];
const symbologyMessage = `symbology must be one of: ${ARTWORK_SYMBOLOGIES.join(", ")}.`;
const formatMessage = `format must be one of: ${ARTWORK_FORMATS.join(", ")}.`;
const presetMessage = `applicationPreset must be one of: ${ARTWORK_APPLICATION_PRESETS.join(", ")}. ` +
    `It selects the GS1 symbol specification table the size is judged against, so it is a ` +
    `statement about where the pack is scanned rather than a display setting.`;
const xDimensionMessage = "xDimensionMm is the module width in millimetres and must be a positive number. It is " +
    "never defaulted: a symbol whose physical size was inferred is a symbol that scans at " +
    "the wrong size.";
/** A scanned qualifier, held to GS1 charset X. Same spelling as the link writer's grain. */
const qualifier = (key) => {
    const message = `${key} must be a string in GS1 charset X — it goes into the Digital Link URI the ` +
        `symbol encodes, and the standard restricts which characters may appear there.`;
    return z.string(message).regex(CHARSET_X, message).optional();
};
/**
 * The qualifiers that appear in the encoded URI, in the order GS1 requires.
 *
 * The **order** is `urls.ts`'s business, not this schema's — `digitalLinkPath` spells
 * `22`/`10`/`21` once for the whole repository, because a path built the wrong way round is
 * a URI the resolver refuses rather than a stylistic variant.
 */
const qualifiersSchema = z
    .object({ cpv: qualifier("cpv"), lot: qualifier("lot"), serial: qualifier("serial") })
    .optional();
const QUANTITY_MESSAGE = "confirmedQuantity, if you send it, is roughly how many units you expect to print with " +
    "this code. It is recorded with the artwork and is never checked against anything — a " +
    "rough number is more useful here than none.";
/**
 * One symbol, synchronously (§8: *"single-symbol = synchronous in the API (<50 ms)"*).
 *
 * `eccLevel` is accepted on both symbologies and **means something on only one of them**.
 * `packages/barcode`'s `SymbolRequest` makes that a property of the type — its Data Matrix
 * arm has no such member, because ECC200 is fixed by ISO/IEC 16022 and offering the choice
 * would be offering a setting that does not exist. A zod discriminated union would say the
 * same thing and would answer a Data Matrix request carrying `eccLevel: "H"` with a 400,
 * which is a **refusal for a field that changes nothing** — the wrong trade on a wire an
 * integrator scripts against. So it is accepted and ignored for Data Matrix, and the op
 * builds the union arm that the symbology allows.
 */
/**
 * **Which symbol** — the fields shared by the artwork request and the placement sheet's.
 *
 * Spelled once and spread into both, rather than written twice, and the reason is the
 * ruling's own wording: the sheet's request is *"the artwork request minus `format` and minus
 * `confirmedQuantity`"*. A second hand-written object that happened to agree today would make
 * that sentence a coincidence — and this repository has already paid for one of those
 * (c95a's two spellings of the covering-prefix predicate, agreeing because one was written by
 * reading the other, with nothing forcing them to go on agreeing).
 *
 * The two subtractions are each a statement rather than a trim:
 *
 *   - **`format`**, because the sheet is not one. See `packages/barcode`'s `ArtworkTarget`
 *     for the mechanism that makes enrolling it in the enum an actual defect rather than an
 *     untidiness.
 *   - **`confirmedQuantity`**, because it is *"roughly how many units you expect to print
 *     with this code"* — a fact about a **print run**. A placement sheet is a document about
 *     where a symbol goes; there is no run of it to size, and offering the field would invite
 *     a number that means nothing on the record it lands on.
 *
 * `escapeHatch` **is** shared, and that is not an oversight: §8.1 row 1 blocks the sheet
 * exactly as it blocks the artwork (see the op's gate walk on why row 2 is the only
 * exemption), so a customer blocked on a never-live domain reaches the sheet by the same
 * §8.3 door, with the same disclosure, and `media.meta.escape_hatch` records it identically.
 */
const symbolSubjectShape = {
    productId: z.uuid("productId must be the id of a product in this organisation."),
    symbology: z.enum(ARTWORK_SYMBOLOGIES, symbologyMessage),
    xDimensionMm: z
        .number(xDimensionMessage)
        .positive(xDimensionMessage)
        .finite(xDimensionMessage),
    applicationPreset: z
        .enum(ARTWORK_APPLICATION_PRESETS, presetMessage)
        .default("retail-pos"),
    /** Omitted means M (§8). Ignored for Data Matrix — see the docblock. */
    eccLevel: z.enum(ARTWORK_ECC_LEVELS).optional(),
    qualifiers: qualifiersSchema,
    /**
     * §8.3 — generate against the platform URI instead of the organisation's own domain.
     *
     * **An explicit opt-in, never a fallback.** The whole of §8.3 is that this choice has a
     * cost which *"must be disclosed at the moment, not buried"*: taken silently it converts
     * a user from the trust path to the lock-in path over impatience. A request that
     * defaulted to it — or an API that quietly fell back to it when a domain gate failed —
     * would be the silent version, made by us on the customer's behalf, for codes that
     * cannot be reprinted.
     */
    escapeHatch: z.boolean().default(false),
};
export const artworkRequestSchema = z
    .object({
    ...symbolSubjectShape,
    format: z.enum(ARTWORK_FORMATS, formatMessage),
    /**
     * How many units the customer says they are about to print — D12, optional, and the
     * copy is deliberately weak until Q-S1 is ruled.
     *
     * **The field ships either way and the wording does not.** If Cameron rules yes it
     * feeds two things worth having: a deletion dialog that can say what a code is on, and
     * a recall's blast radius. If he rules no, an optional member nobody sends costs
     * nothing and comes out in one edit. What would have been expensive is the other
     * order — adding it after `media.meta` had a population of artwork rows without it,
     * which is `0009`'s own reason for anticipating the member in its column comment.
     *
     * Nothing validates it against reality and nothing can: it is a number a person typed
     * about a print run that has not happened. So the message below asks rather than
     * asserts, and no gate reads it.
     */
    confirmedQuantity: z
        .number(QUANTITY_MESSAGE)
        .int(QUANTITY_MESSAGE)
        .min(0, QUANTITY_MESSAGE)
        .optional(),
})
    .describe("POST /v1/artwork — one symbol, synchronously. The response is the file.");
/**
 * `POST /v1/artwork/placement-sheet` — the dual-marking placement sheet, on its own door.
 *
 * §8 promises the sheet is *"generated alongside"* the artwork; §8.2's fifth bullet makes it
 * *"free at every tier … Gating it would gate correctness."* Ruled 2026-08-21
 * ([[2026-08-21 The Placement Sheet Gets Its Own Door]]): a **sibling request**, not a fifth
 * format and not a bundled multipart response.
 *
 * ## Why a second endpoint rather than a fifth format
 *
 * Two independent reasons, and the first is a live defect rather than a preference.
 * `packages/barcode`'s `ArtworkTarget` carries it in full: §8.1 row 2's format scope is
 * implemented as `isPrintDeliverable(format)`, so a `"placement"` member of
 * {@link ARTWORK_FORMATS} would be classified a print deliverable and **withheld from an
 * unverified prefix** — the one outcome §8.2's fifth bullet forbids. The second is shape: the
 * sheet takes `(request, matrix)` and is not an `Emitter`.
 *
 * And not bundled bytes, because a single-file response would have to become multipart —
 * hostile to every integrator scripting the wire — an SVG-only consumer would pay a render, a
 * storage PUT and a `media` row for a document it discards, and the sheet is a function of the
 * **symbol** rather than of the format, so four format downloads would generate it four times
 * to be deduped by content-addressing.
 *
 * ## The response is `application/pdf`, and the gates are the artwork's
 *
 * The op shares `loadSubjects` and `evaluateGates` with `generateArtwork` verbatim and differs
 * in exactly one policy position: **row 2 does not withhold it.** Every other §8.1 row blocks
 * the sheet, including row 4 — a placement sheet for a sub-minimum symbol would be a
 * conformance document for a nonconformant symbol, which is worse than no sheet.
 *
 * §8's *"generated alongside"* is kept **at the screen**: `ArtworkGenerator` offers the sheet
 * as a checkbox in the file list, on the grain the screen already has, and ticking it fires
 * this request beside the artwork one.
 */
export const placementSheetRequestSchema = z
    .object(symbolSubjectShape)
    .describe("POST /v1/artwork/placement-sheet — the dual-marking placement sheet for one symbol. " +
    "The response is the PDF.");
/**
 * The most products one batch may name.
 *
 * §5 caps the sibling bulk endpoint (`PATCH /products:batch`) at 500 and §9's import
 * pipeline is sized for 50,000 rows, so this is the smaller, request-shaped bound rather
 * than a catalogue-shaped one: every row here costs a gate evaluation, and gate 1 and the
 * escape-hatch check each make a **live HTTP probe**. 500 of those in one request is a
 * request that holds a connection open for minutes.
 */
export const MAX_ARTWORK_BATCH_PRODUCTS = 500;
/**
 * `POST /v1/artwork/batches` — **gate evaluation for many products, and no ZIP.**
 *
 * §5 describes this as returning a job and §8.2 gates batch ZIP generation at Starter and
 * above. The ZIP job is deferred to Phase 5 with the tier that gates it (plan D9), and the
 * reasoning is worth restating where the shape is defined rather than only in the plan:
 * until billing exists there are no Starter accounts, so a gate honoured today would be a
 * gate over an empty set, and **nothing correctness-shaped is postponed** — single-symbol
 * vectors are free at every tier and ship now.
 *
 * What is *not* deferred is the part §8.1 makes a contract: *"Batch requests return
 * per-GTIN gate results so a 400-SKU batch reports which rows were withheld rather than
 * failing whole."* That is this endpoint, and it is the half a customer with a catalogue
 * actually needs first — knowing which forty of four hundred SKUs cannot be printed is the
 * work; producing the ZIP is the easy part that follows it.
 */
export const artworkBatchRequestSchema = z
    .object({
    productIds: z
        .array(z.uuid("Every entry in productIds must be a product id."))
        .min(1, "A batch names at least one product.")
        .max(MAX_ARTWORK_BATCH_PRODUCTS, `A batch names at most ${String(MAX_ARTWORK_BATCH_PRODUCTS)} products. Each row ` +
        `costs a live probe of the domain it would be printed against, so a larger ` +
        `batch is a request that holds open for minutes rather than one that is refused ` +
        `on principle.`),
    symbology: z.enum(ARTWORK_SYMBOLOGIES, symbologyMessage),
    format: z.enum(ARTWORK_FORMATS, formatMessage),
    xDimensionMm: z
        .number(xDimensionMessage)
        .positive(xDimensionMessage)
        .finite(xDimensionMessage),
    applicationPreset: z
        .enum(ARTWORK_APPLICATION_PRESETS, presetMessage)
        .default("retail-pos"),
    eccLevel: z.enum(ARTWORK_ECC_LEVELS).optional(),
    /**
     * Ask for the ZIP as well as the rows — §8.2's *"batch generation (ZIP) is Starter and
     * above"*, U62.
     *
     * **Opt-in, and `false` is what keeps the preflight free.** The rows are the preflight
     * a catalogue owner needs at every tier, and §8.1's API sentence requires this endpoint
     * to answer them; the ZIP is the paid half. A request that always queued a job would
     * either gate the preflight — which §8.1 forbids — or render a catalogue nobody asked
     * for every time a screen checked one product's gates.
     *
     * With it set, a caller below `BATCH_ZIP_TIER` is refused `PLAN_GATED` **before** the
     * gates are walked: no probe is made and no row is evaluated for a job that cannot run.
     */
    zip: z.boolean().default(false),
})
    .describe("POST /v1/artwork/batches — per-product gate results, free at every tier, plus the " +
    "ZIP job when `zip` is set (Starter and above).");
/**
 * The marks a generated file can carry — set-equal to `packages/barcode`'s
 * `WATERMARK_KINDS`, asserted in `apps/api` for the reason the header gives about the
 * three vocabularies above.
 *
 * ## Why this is on the wire at all, rather than derived by the screen
 *
 * One of the two is derivable from the request and the other is not. *"Every PNG is
 * watermarked"* is a function of `format` alone, so a screen could compute it — and
 * `screens.md` §6's other watermark state, **unverified prefix**, is a function of a
 * database column the dashboard never sees. A screen that derived the first and guessed
 * the second would render one true sentence beside one hopeful one, which is the shape
 * `gate.ts` was built to prevent for refusals.
 *
 * So the whole set is stated by the side that knows, from `watermarksFor` — the single
 * place §8.2's two rules are applied — and both consumers read the same value: the
 * preflight row carries it as data, and `POST /v1/artwork` projects it into
 * `x-packagepage-watermarks` beside the file it describes.
 */
export const ARTWORK_WATERMARKS = ["raster-preview", "unverified-prefix"];
//# sourceMappingURL=artwork.js.map